import React from "react";
import { Grid, Typography, Box } from "@mui/material";
import {
  BCPCalendarDays,
  BCPDiceD20,
  BCPChartMixed
} from "../../../components/icons/bcp/BCPIcons";
import StatTable from "./StatTable";

const HERO_ICONS = [BCPCalendarDays, BCPDiceD20, BCPChartMixed];
const HERO_COLORS = ["#8b5cf6", "#3b82f6", "#4ade80"];

export default function OverallStats({ data, theme }) {
  if (!data || !data.charts) return null;

  const isDark = theme.palette.mode === "dark";

  const totalChart = data.charts.find(c => c.label === "Total");

  const heroStats = [];
  if (totalChart) {
    const headerRow = totalChart.rows.find(r => r.header);
    const dataRows = totalChart.rows.filter(r => !r.header);
    const colLabels = headerRow?.cols.slice(1) || [];

    colLabels.forEach((label, idx) => {
      const colIdx = idx + 1;
      const values = dataRows
        .map(r => r.cols[colIdx])
        .filter(v => v !== "-" && v !== null && v !== undefined && !isNaN(Number(v)))
        .map(Number);

      if (values.length > 0) {
        const total = values.reduce((a, b) => a + b, 0);
        const displayValue =
          label === "Games per Event"
            ? Math.round(total / values.length).toLocaleString()
            : total.toLocaleString();
        heroStats.push({ label, value: displayValue });
      }
    });
  }

  const heroColSize = heroStats.length > 0 ? Math.floor(12 / heroStats.length) : 4;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          color: theme.palette.text.primary,
          fontWeight: 600,
          textAlign: "center",
          letterSpacing: "0.05em"
        }}
      >
        Event Rundown
      </Typography>
      {heroStats.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {heroStats.map((stat, i) => {
            const Icon = HERO_ICONS[i];
            const color = HERO_COLORS[i] || "#8b5cf6";
            return (
              <Grid item xs={12} sm={heroColSize} key={i}>
                <Box
                  sx={{
                    backgroundColor: isDark
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.02)",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                    borderTop: `3px solid ${color}`,
                    borderRadius: 1,
                    px: 3,
                    py: 2.5,
                    textAlign: "center",
                    boxShadow: isDark
                      ? "0 2px 8px rgba(0,0,0,0.3)"
                      : "0 2px 8px rgba(0,0,0,0.1)"
                  }}
                >
                  {Icon && (
                    <Box sx={{ mb: 1.5 }}>
                      <Icon size="lg" style={{ color }} />
                    </Box>
                  )}
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      lineHeight: 1,
                      mb: 0.75
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      fontWeight: 600,
                      fontSize: "0.65rem"
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Grid container spacing={3}>
        {data.charts.map((chart, idx) => {
          const count = data.charts.length;
          const isLastOdd = count % 2 !== 0 && idx === count - 1;
          return (
            <Grid item xs={12} md={isLastOdd ? 12 : 6} key={chart.label}>
              <StatTable title={chart.label} rows={chart.rows} theme={theme} />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
